// Dialog Manager - Handles all dialog interactions
export class DialogManager {
  private currentDialog: HTMLElement | null = null;
  private dialogStack: HTMLElement[] = [];
  private isAnimating = false;

  constructor() {
    this.init();
  }

  init(): void {
    // Setup dialog close handlers
    this.setupDialogCloseHandlers();

    // Setup backdrop click handlers
    this.setupBackdropHandlers();

    // Setup keyboard handlers
    this.setupKeyboardHandlers();
  }

  openDialog(dialogId: string, data?: any): void {
    if (this.isAnimating) return;

    const dialog = document.getElementById(dialogId);
    if (!dialog) {
      console.error(`Dialog with id "${dialogId}" not found`);
      return;
    }

    // Close current dialog if open
    if (this.currentDialog) {
      this.closeDialog();
    }

    this.currentDialog = dialog;
    this.dialogStack.push(dialog);

    // Check if it's a drawer or dialog
    if (
      dialog.classList.contains("cart-drawer") ||
      dialog.tagName.toLowerCase() === "glass-drawer"
    ) {
      this._showDrawer(dialog, data);
    } else {
      this.showDialog(dialog, data);
    }
  }

  closeDialog(): void {
    if (this.isAnimating || !this.currentDialog) return;

    const dialog = this.currentDialog;
    this.hideDialog(dialog);

    // Remove from stack
    this.dialogStack.pop();

    // Set new current dialog
    this.currentDialog =
      this.dialogStack.length > 0
        ? this.dialogStack[this.dialogStack.length - 1]
        : null;
  }

  closeAllDialogs(): void {
    while (this.dialogStack.length > 0) {
      this.closeDialog();
    }
  }

  closeCurrentDialog(): void {
    this.closeDialog();
  }

  private showDialog(dialog: HTMLElement, data?: any): void {
    this.isAnimating = true;

    // Add to DOM if not already there
    if (!dialog.parentNode) {
      document.body.appendChild(dialog);
    }

    // Set data if provided
    if (data) {
      dialog.setAttribute("data-dialog-data", JSON.stringify(data));
    }

    // Show dialog - set aria-hidden to false BEFORE focusing
    dialog.classList.add("dialog-open");
    dialog.setAttribute("aria-hidden", "false");
    dialog.setAttribute("aria-modal", "true");

    // Animate in
    requestAnimationFrame(() => {
      dialog.classList.add("dialog-enter");

      // Trigger custom event FIRST so dialog can load data
      dialog.dispatchEvent(new CustomEvent("dialog:open", { detail: data }));

      // Focus management AFTER aria-hidden is set and dialog is visible
      setTimeout(() => {
        this.trapFocus(dialog);
        this.isAnimating = false;
      }, 50);
    });

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  showDrawer(drawerId: string, data?: any): void {
    const drawer = document.getElementById(drawerId);
    if (!drawer) {
      console.error(`Drawer with id "${drawerId}" not found`);
      return;
    }
    this._showDrawer(drawer, data);
  }

  private _showDrawer(drawer: HTMLElement, data?: any): void {
    this.isAnimating = true;

    // Add to DOM if not already there
    if (!drawer.parentNode) {
      document.body.appendChild(drawer);
    }

    // Set data if provided
    if (data) {
      drawer.setAttribute("data-dialog-data", JSON.stringify(data));
    }

    // Show drawer
    drawer.classList.add("show");
    drawer.setAttribute("aria-hidden", "false");
    drawer.setAttribute("aria-modal", "true");

    // Focus management
    this.trapFocus(drawer);

    // Trigger custom event
    drawer.dispatchEvent(new CustomEvent("drawer:open", { detail: data }));

    setTimeout(() => {
      this.isAnimating = false;
    }, 300);

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  private hideDialog(dialog: HTMLElement): void {
    this.isAnimating = true;

    // Check if it's a drawer or dialog
    if (
      dialog.classList.contains("cart-drawer") ||
      dialog.tagName.toLowerCase() === "glass-drawer"
    ) {
      this._hideDrawer(dialog);
    } else {
      this.hideDialogElement(dialog);
    }
  }

  private hideDialogElement(dialog: HTMLElement): void {
    // Animate out
    dialog.classList.add("dialog-exit");

    // Blur any focused element inside the dialog BEFORE setting aria-hidden
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && dialog.contains(activeElement)) {
      activeElement.blur();
    }

    // Set aria-hidden AFTER removing focus to prevent accessibility warning
    dialog.setAttribute("aria-hidden", "true");

    // Trigger custom event
    dialog.dispatchEvent(new CustomEvent("dialog:close"));

    setTimeout(() => {
      dialog.classList.remove("dialog-open", "dialog-enter", "dialog-exit");
      dialog.removeAttribute("aria-modal");

      // Remove data
      dialog.removeAttribute("data-dialog-data");

      this.isAnimating = false;

      // Restore body scroll
      document.body.style.overflow = "";

      // Return focus to trigger element
      this.returnFocus();
    }, 300);
  }

  private _hideDrawer(drawer: HTMLElement): void {
    // Blur any focused element inside the drawer BEFORE setting aria-hidden
    // This prevents accessibility warnings about aria-hidden on focused elements
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && drawer.contains(activeElement)) {
      activeElement.blur();
    }

    // Hide drawer
    drawer.classList.remove("show");
    // Set aria-hidden AFTER removing focus to prevent accessibility warning
    drawer.setAttribute("aria-hidden", "true");
    drawer.removeAttribute("aria-modal");

    // Trigger custom event
    drawer.dispatchEvent(new CustomEvent("drawer:close"));

    // Remove data
    drawer.removeAttribute("data-dialog-data");

    this.isAnimating = false;

    // Restore body scroll
    document.body.style.overflow = "";

    // Return focus to trigger element
    this.returnFocus();
  }

  private setupDialogCloseHandlers(): void {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Close button - check if clicked element or its parent has the close class
      const closeButton = target.closest(".dialog-close, .drawer-close");
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        this.closeDialog();
      }
    });
  }

  private setupBackdropHandlers(): void {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Backdrop click
      if (
        target.classList.contains("dialog-backdrop") ||
        target.classList.contains("drawer-backdrop")
      ) {
        e.preventDefault();
        this.closeDialog();
      }
    });
  }

  private setupKeyboardHandlers(): void {
    document.addEventListener("keydown", (e) => {
      if (!this.currentDialog) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this.closeDialog();
          break;
        case "Tab":
          this.handleTabKey(e);
          break;
      }
    });
  }

  private handleTabKey(e: KeyboardEvent): void {
    if (!this.currentDialog) return;

    const focusableElements = this.getFocusableElements(this.currentDialog);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  private trapFocus(dialog: HTMLElement): void {
    const focusableElements = this.getFocusableElements(dialog);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "a[href]",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    return Array.from(
      container.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];
  }

  private returnFocus(): void {
    // Return focus to the element that opened the dialog
    const lastFocusedElement = document.querySelector(
      "[data-last-focused]"
    ) as HTMLElement;
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement.removeAttribute("data-last-focused");
    }
  }

  // Store the currently focused element before opening dialog
  storeFocus(): void {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      activeElement.setAttribute("data-last-focused", "true");
    }
  }

  // Get current dialog
  getCurrentDialog(): HTMLElement | null {
    return this.currentDialog;
  }

  // Check if dialog is open
  isDialogOpen(): boolean {
    return this.currentDialog !== null;
  }

  // Get dialog data
  getDialogData(): any {
    if (!this.currentDialog) return null;

    const dataAttr = this.currentDialog.getAttribute("data-dialog-data");
    if (!dataAttr) return null;

    try {
      return JSON.parse(dataAttr);
    } catch {
      return null;
    }
  }

  // Show notification
  showNotification(
    message: string,
    type: "success" | "error" | "info" = "info"
  ): void {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `notification notification--${type}`;
    notification.textContent = message;

    // Accessibility: ARIA roles for live announcements
    if (type === "error") {
      notification.setAttribute("role", "alert");
      notification.setAttribute("aria-live", "assertive");
    } else {
      notification.setAttribute("role", "status");
      notification.setAttribute("aria-live", "polite");
    }

    // Add to page
    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add("notification--show");
    });

    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.classList.remove("notification--show");
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  }
}
